import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ensureSpSessionHydrated } from '@/features/auth/api/useAuth'
import { AlertStatusModal } from '@/shared/components/confirmation'
import { useSessionStore } from '@/stores/useSessionStore'
import { runDetached } from '@/shared/lib/runDetachedPromise'

interface InstrumentLinkBlockedDialogProps {
  readonly open: boolean
  readonly headline: string
  readonly message: string
}

/** Invalid / expired instrument link — modal with auth-aware exit (BFP-257 / BFP-300). */
export function InstrumentLinkBlockedDialog({
  open,
  headline,
  message,
}: InstrumentLinkBlockedDialogProps) {
  const navigate = useNavigate()
  const storeAuthenticated = useSessionStore((state) => state.isAuthenticated)
  const [authReady, setAuthReady] = useState(storeAuthenticated)
  const [isAuthenticated, setIsAuthenticated] = useState(storeAuthenticated)

  useEffect(() => {
    if (storeAuthenticated) {
      setIsAuthenticated(true)
      setAuthReady(true)
      return
    }

    let cancelled = false
    runDetached(
      ensureSpSessionHydrated().then((ok) => {
        if (!cancelled) {
          setIsAuthenticated(ok)
          setAuthReady(true)
        }
      }),
    )

    return () => {
      cancelled = true
    }
  }, [storeAuthenticated])

  const goExit = () => {
    const authed = useSessionStore.getState().isAuthenticated
    navigate(authed ? '/' : '/login', { replace: true })
  }

  if (!authReady) {
    return null
  }

  return (
    <AlertStatusModal
      open={open}
      headline={headline}
      bodyLines={[message]}
      primaryLabel={isAuthenticated ? 'Back to home' : 'Back to login'}
      onPrimary={goExit}
      onClose={goExit}
      dialogTestId="instrument-link-blocked-dialog"
      primaryTestId={
        isAuthenticated ? 'instrument-link-back-home' : 'instrument-link-back-login'
      }
    />
  )
}
